import type { JSX } from 'react';
import {
  AlertTriangle,
  Building2,
  Film,
  Heart,
  Newspaper,
  Scissors,
  User,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tag } from '@/components/ui/tag';
import type { AccountType, DiscoveryMethod } from '../api/types';
import { soundRefFromSource } from '../lib/sounds';
import { SoundLink } from './SoundLink';

/**
 * What kind of account this is.
 *
 * Rendered as a `<Tag>` (metadata) rather than a `<Pill>` (state), per
 * DESIGN.md — a creator's type is a property of who they are, not where they
 * are in a funnel.
 *
 * A rule-based classifier will always get edge cases wrong, so low-confidence
 * calls are visually softened and the reasons are on hover. It flags; you
 * decide.
 */

const TYPE_META: Record<AccountType, { label: string; icon: typeof User; offTarget?: boolean }> = {
  editor: { label: 'Editor', icon: Film },
  clipper: { label: 'Clipper', icon: Scissors },
  fan_page: { label: 'Fan page', icon: Heart },
  news_gossip: { label: 'News / gossip', icon: Newspaper, offTarget: true },
  brand_official: { label: 'Official', icon: Building2, offTarget: true },
  personal: { label: 'Personal', icon: User },
  unknown: { label: 'Unknown', icon: User },
};

export const ACCOUNT_TYPE_ORDER: AccountType[] = [
  'editor',
  'clipper',
  'personal',
  'fan_page',
  'news_gossip',
  'brand_official',
  'unknown',
];

export function accountTypeLabel(t: AccountType): string {
  return TYPE_META[t]?.label ?? t;
}

/** Types that are almost never a valid outreach target. */
export function isOffTarget(t: AccountType | null | undefined): boolean {
  return !!t && !!TYPE_META[t]?.offTarget;
}

export function AccountTypeBadge({
  accountType,
  confidence,
  signals,
}: {
  accountType: AccountType | null;
  confidence?: number | null;
  signals?: string[] | null;
}): JSX.Element {
  if (!accountType) return <span className="text-muted-foreground">—</span>;
  const meta = TYPE_META[accountType] ?? TYPE_META.unknown;
  const Icon = meta.icon;
  const unsure = (confidence ?? 1) < 0.6;

  const title = [
    `${meta.label}${confidence != null ? ` · ${Math.round(confidence * 100)}% confident` : ''}`,
    ...(signals?.length ? [signals.join('; ')] : []),
    ...(unsure ? ['Low confidence — worth a look before acting on it.'] : []),
  ].join('\n');

  return (
    <span className="inline-flex items-center gap-1" title={title}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium leading-none',
          meta.offTarget
            ? 'border-destructive/35 bg-destructive/10 text-destructive'
            : 'border-border bg-muted/40 text-foreground/80',
          unsure && 'opacity-70',
        )}
      >
        <Icon className="h-3 w-3 shrink-0" strokeWidth={2} />
        {meta.label}
        {unsure && <span className="opacity-70">?</span>}
      </span>
      {meta.offTarget && (
        <AlertTriangle
          className="h-3 w-3 shrink-0 text-destructive"
          aria-label="Probably not an outreach target"
        />
      )}
    </span>
  );
}

const METHOD_META: Record<DiscoveryMethod, { label: string; hint: string }> = {
  keyword: { label: 'Search', hint: 'Found by a keyword search you ran in this folder' },
  hashtag: { label: 'Hashtag', hint: 'Found by a hashtag search' },
  sound: { label: 'Sound', hint: 'Found by searching a TikTok sound' },
  lookalike: { label: 'Lookalike', hint: 'Found by a lookalike run off an existing folder' },
  csv: { label: 'Import', hint: 'Imported from a CSV' },
  manual: { label: 'Manual', hint: 'Added by hand' },
  repost: { label: 'Repost', hint: "Found in the reposts of this folder's creators" },
  similar: { label: 'Similar', hint: "The platform's own similar-accounts suggestion for a seed account" },
  location: { label: 'Location', hint: 'Posted from a geotagged place' },
};

export const DISCOVERY_METHOD_ORDER: DiscoveryMethod[] = [
  'keyword',
  'lookalike',
  'hashtag',
  'sound',
  'similar',
  'repost',
  'location',
  'csv',
  'manual',
];

export function discoveryMethodLabel(m: DiscoveryMethod): string {
  return METHOD_META[m]?.label ?? m;
}

/**
 * How this creator got into this folder — the answer to "did I search for
 * this person, or did a lookalike run bring them in?".
 */
export function FoundViaBadge({
  method,
  source,
  topicMatch,
}: {
  method: DiscoveryMethod;
  source: string | null;
  topicMatch?: number | null;
}): JSX.Element {
  const meta = METHOD_META[method] ?? METHOD_META.keyword;
  // "sound:<id>" (TikTok) / "audio:<id>" (Instagram): the sound itself, linked and playable.
  const sound = soundRefFromSource(source);
  const title = [
    meta.hint,
    source ? `Source: ${source}` : null,
    topicMatch != null
      ? `${Math.round(topicMatch * 100)}% of their recent posts match this search`
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <Tag tone={method === 'lookalike' ? 'niche' : 'neutral'}>{meta.label}</Tag>
      {sound ? (
        <span className="max-w-[9rem] truncate text-xs text-muted-foreground">
          <SoundLink platform={sound.platform} id={sound.id} />
        </span>
      ) : (
        source && <span className="max-w-[9rem] truncate text-xs text-muted-foreground">{source}</span>
      )}
    </span>
  );
}
